<script lang="ts">
	import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
	import emblaCarouselSvelte from 'embla-carousel-svelte';
	import { onMount, type Snippet } from 'svelte';
	import { browser } from '$app/environment';
	import Autoplay from 'embla-carousel-autoplay';
	import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
	import type { ClassValue } from 'svelte/elements';

	interface Props {
		align?: EmblaOptionsType['align'];
		loop?: boolean;
		currentIndex?: number;
		active?: boolean;
		draggable?: boolean;
		autoplay?: boolean;
		autoplayDuration?: number;
		overflow?: boolean;
		containerClass?: ClassValue;
		slidesToScroll?: number;
		breakpoints?: EmblaOptionsType['breakpoints'];
		id?: string;
		onchange?: (index: number) => void;
		class?: ClassValue;
		children?: Snippet;
		embla?: EmblaCarouselType;
	}

	let {
		align = 'center',
		loop = false,
		currentIndex = $bindable(0),
		active = true,
		draggable = true,
		autoplay = false,
		autoplayDuration = 4000,
		overflow = false,
		containerClass,
		slidesToScroll = 1,
		breakpoints = {},
		id,
		onchange,
		class: className,
		children,
		embla = $bindable()
	}: Props = $props();

	let config: { options: EmblaOptionsType; plugins: EmblaPluginType[] } = $state({
		options: {},
		plugins: []
	});

	let resizeTimer: ReturnType<typeof setTimeout>;

	function setProgress() {
		if (!embla) return;

		currentIndex = embla.selectedScrollSnap();
		onchange?.(currentIndex);
		if (autoplay && !embla.plugins().autoplay.isPlaying()) {
			const autoplayPlugin = embla.plugins().autoplay;
			autoplayPlugin.reset();
			autoplayPlugin.play();
		}
	}

	function onInit(e: CustomEvent<EmblaCarouselType>) {
		embla = e.detail;

		if (!embla) throw new Error('onInit: Something went wrong...');

		active = embla.scrollSnapList().filter(Boolean).length > 0;

		embla.on('select', setProgress);
		embla.on('reInit', setProgress);
		addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(setProgress, 250);
		});
	}

	$effect(() => {
		if (!embla) return;

		if (autoplay) {
			const autoplayPlugin = embla.plugins().autoplay;
			autoplayPlugin.options.delay = autoplayDuration;
			autoplayPlugin.play();
		} else {
			embla.plugins().autoplay.stop();
		}
	});

	onMount(() => {
		config = {
			options: {
				active,
				breakpoints,
				align,
				loop,
				containScroll: 'trimSnaps',
				skipSnaps: true,
				startIndex: currentIndex,
				slidesToScroll,
				watchDrag: draggable
			} satisfies EmblaOptionsType,
			plugins: [
				Autoplay({
					active: true,
					delay: autoplayDuration,
					stopOnInteraction: true,
					playOnInit: autoplay
				}),
				WheelGesturesPlugin()
			]
		};

		return () => {
			if (browser) {
				removeEventListener('resize', setProgress);
				clearTimeout(resizeTimer);
			}
			embla?.destroy();
		};
	});
</script>

<div
	class={[
		...(Array.isArray(containerClass) ? containerClass : [containerClass]),
		'transition-opacity duration-300',
		{ 'opacity-5': !embla, 'overflow-visible': overflow, 'overflow-hidden': !overflow }
	]}
	use:emblaCarouselSvelte={config}
	onemblaInit={onInit}
	{id}
>
	<div
		class={[
			{ 'lg:cursor-grab': active && draggable },
			...(Array.isArray(className) ? className : [className])
		]}
	>
		{@render children?.()}
	</div>
</div>
